export type OnboardingIntent = "explore" | "create" | "both";

export type OnboardingTopicKey =
  | "tech" | "design" | "music" | "gaming" | "film_tv" | "comedy" | "news"
  | "business" | "science" | "health_fitness" | "food" | "sports" | "art"
  | "faith" | "education" | "travel";

export interface OnboardingTopic {
  key: OnboardingTopicKey;
  label: string;
  icon: string; // lucide-react-native icon name
  podcastCategories: string[]; // matches the podcasts.category enum values
  keywords: string[]; // lowercase substrings matched against bio/hashtags/tags
}

// Draft set — tune labels/keywords against the real creator base before launch.
export const ONBOARDING_TOPICS: OnboardingTopic[] = [
  { key: "tech", label: "Tech", icon: "Cpu", podcastCategories: ["Technology"], keywords: ["tech", "ai", "startup", "coding", "developer", "software", "gadget"] },
  { key: "design", label: "Design", icon: "Palette", podcastCategories: ["Arts"], keywords: ["design", "ux", "ui", "typography", "brand", "figma"] },
  { key: "music", label: "Music", icon: "Music", podcastCategories: ["Music"], keywords: ["music", "producer", "beats", "guitar", "song", "dj"] },
  { key: "gaming", label: "Gaming", icon: "Gamepad2", podcastCategories: ["Leisure"], keywords: ["gaming", "gamer", "esports", "twitch", "speedrun"] },
  { key: "film_tv", label: "Film & TV", icon: "Clapperboard", podcastCategories: ["TV & Film"], keywords: ["film", "movie", "cinema", "tv", "series", "review"] },
  { key: "comedy", label: "Comedy", icon: "Laugh", podcastCategories: ["Comedy"], keywords: ["comedy", "comedian", "funny", "standup", "sketch", "meme"] },
  { key: "news", label: "News", icon: "Newspaper", podcastCategories: ["News", "Government"], keywords: ["news", "politics", "current", "journalism", "world"] },
  { key: "business", label: "Business", icon: "Briefcase", podcastCategories: ["Business"], keywords: ["business", "founder", "marketing", "finance", "investing", "entrepreneur"] },
  { key: "science", label: "Science", icon: "FlaskConical", podcastCategories: ["Science"], keywords: ["science", "space", "physics", "biology", "research"] },
  { key: "health_fitness", label: "Health & Fitness", icon: "Dumbbell", podcastCategories: ["Health"], keywords: ["fitness", "health", "gym", "workout", "nutrition", "wellness", "running"] },
  { key: "food", label: "Food", icon: "UtensilsCrossed", podcastCategories: ["Leisure"], keywords: ["food", "cooking", "recipe", "chef", "baking", "restaurant"] },
  { key: "sports", label: "Sports", icon: "Trophy", podcastCategories: ["Sports"], keywords: ["sports", "football", "basketball", "soccer", "nba", "nfl", "athlete"] },
  { key: "art", label: "Art", icon: "Brush", podcastCategories: ["Arts"], keywords: ["art", "artist", "illustration", "painting", "drawing", "sculpture"] },
  { key: "faith", label: "Faith", icon: "Sparkle", podcastCategories: ["Religion"], keywords: ["faith", "christian", "spiritual", "prayer", "bible", "meditation"] },
  { key: "education", label: "Education", icon: "GraduationCap", podcastCategories: ["Education"], keywords: ["education", "learning", "teacher", "study", "tutorial", "explainer"] },
  { key: "travel", label: "Travel", icon: "Plane", podcastCategories: ["Leisure"], keywords: ["travel", "trip", "vanlife", "adventure", "wanderlust", "nomad"] },
];

const TOPIC_BY_KEY = new Map(ONBOARDING_TOPICS.map((t) => [t.key, t]));
export function topicByKey(key: string): OnboardingTopic | undefined {
  return TOPIC_BY_KEY.get(key as OnboardingTopicKey);
}

export interface CreatorSignals {
  bio?: string | null;
  podcastCategories?: string[];
  hashtags?: string[];
  articleTags?: string[];
}

// Deterministic overlap score. Podcast-category match is the strongest signal (+2);
// bio/hashtag/article-tag keyword matches each add +1 (once per interest+source).
export function scoreCreatorForInterests(
  signals: CreatorSignals,
  interestKeys: string[],
  topics: OnboardingTopic[] = ONBOARDING_TOPICS,
): number {
  const byKey = new Map(topics.map((t) => [t.key, t]));
  const bio = (signals.bio ?? "").toLowerCase();
  const hashtags = (signals.hashtags ?? []).map((h) => h.toLowerCase());
  const articleTags = (signals.articleTags ?? []).map((t) => t.toLowerCase());
  const cats = signals.podcastCategories ?? [];
  let score = 0;
  for (const key of interestKeys) {
    const topic = byKey.get(key as OnboardingTopicKey);
    if (!topic) continue;
    if (cats.some((c) => topic.podcastCategories.includes(c))) score += 2;
    if (topic.keywords.some((k) => bio.includes(k))) score += 1;
    if (topic.keywords.some((k) => hashtags.some((h) => h.includes(k)))) score += 1;
    if (topic.keywords.some((k) => articleTags.some((t) => t.includes(k)))) score += 1;
  }
  return score;
}
